import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { LoggingService, PromptConfirmDialogComponent, PromptConfirmInput, PromptConfirmResult } from "@merri/common";
import { Subject, of } from "rxjs";
import { Observable } from "rxjs/internal/Observable";
import { concatMap, map, take, takeUntil, tap } from "rxjs/operators";
import { EventContextService } from "src/app/features/events";
import { OnboardingService, OnboardingWorkflowStepTypes } from "src/app/features/onboarding";
import { RadioButtonSelectionDialogComponent, RadioButtonSelectionInfo } from "src/app/shared";
import { VisionboardItem, VisionboardMode, VisionboardTemplateLane } from "../../models";
import {
  VisionboardAttributesService,
  VisionboardCategoriesService,
  VisionboardItemCompatibilityService,
  VisionboardItemDetailService,
  VisionboardService,
  VisionboardWorkspaceItemsStoreService,
  VisionboardWorkspaceTemplateService,
} from "../../services";
import { ForceVisionboardItemAddRemoveButton } from "../../shared";
import { VisionboardItemAddRemoveClickInput } from "../../shared/models";
import { VisionboardItemAddRemoveButtonService } from "../../shared/services";
import {
  CompatibleItemSelectionInput,
  VisionboardCompatibleItemSelectionDialogComponent,
} from "../visionboard-compatible-item-selection-dialog/visionboard-compatible-item-selection-dialog.component";
import { VisionboardItemSelectionDialogComponent } from "../visionboard-item-selection-dialog/visionboard-item-selection-dialog.component";

@Component({
    selector: "app-visionboard-workspace-template-lane-item",
    templateUrl: "./visionboard-workspace-template-lane-item.component.html",
    styleUrls: ["./visionboard-workspace-template-lane-item.component.scss"],
    providers: [VisionboardItemAddRemoveButtonService],
    standalone: false
})
export class VisionboardWorkspaceTemplateLaneItemComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  @Input() lane: VisionboardTemplateLane;
  @Input() index: number;
  @Input() disableChecks: boolean;
  @Output() itemChange: EventEmitter<[number, VisionboardItem]>;

  triggerOnboarding: boolean;
  acceptsInput: boolean;
  multiSubCatMode: boolean;
  removable: boolean;
  isVenueProvided: boolean;
  venueProvidedLabel: string;
  item: VisionboardItem;
  tooltipText: string;
  /* Determine if the item requires a modal for selection */
  isTypeModalMode: boolean;
  /* 
    Sub-cats that require selection modals.... 
    TODO THIS IS NOT BEING USED ANYMORE SO MAYBE EVENTUALLY NUKE (1/8/21) 
  */
  typeModalSubCategories: string[] = [];
  subCategoryKeys: [string, string][];
  nestedSubCategoryKeys: [string, string[]][];

  isHovered: boolean;
  showMoreActionsMenu: boolean;
  isOrderable: boolean;
  hasOrderManagement: boolean;

  @ViewChild("multiSubCategoryTrigger", { read: MatMenuTrigger }) multiSubCategoryMatMenuTrigger: MatMenuTrigger;
  @ViewChild("nestedMultiSubCategoryTrigger", { read: MatMenuTrigger }) nestedMultiSubCategoryMatMenuTrigger: MatMenuTrigger;

  constructor(
    private visionboardCategoriesService: VisionboardCategoriesService,
    private visionboardAttributesService: VisionboardAttributesService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private visionboardItemCompatibilityService: VisionboardItemCompatibilityService,
    private visionboardWorkspaceTemplateService: VisionboardWorkspaceTemplateService,
    private visionboardService: VisionboardService,
    private matDialog: MatDialog,
    private onboardingService: OnboardingService,
    private _visionboardItemDetailService: VisionboardItemDetailService,
    private _vcr: ViewContainerRef,
    private _visionboardItemAddRemoveButtonService: VisionboardItemAddRemoveButtonService,
    private _eventContextService: EventContextService,
  ) {
    this.venueProvidedLabel = "Venue Provided";
    this.itemChange = new EventEmitter<[number, VisionboardItem]>();
  }

  @HostListener("window:resize")
  onResize(): void {
    if (this.multiSubCategoryMatMenuTrigger && this.multiSubCategoryMatMenuTrigger.menuOpen) {
      this.multiSubCategoryMatMenuTrigger.closeMenu();
    }

    if (this.nestedMultiSubCategoryMatMenuTrigger && this.nestedMultiSubCategoryMatMenuTrigger.menuOpen) {
      this.nestedMultiSubCategoryMatMenuTrigger.closeMenu();
    }
  }

  ngOnInit(): void {
    this.showMoreActionsMenu = this.visionboardService.allowCustomItemEdit();
    this.subCategoryKeys = [];
    this.nestedSubCategoryKeys = [];

    Object.keys(this.lane.categories).forEach(category => {
      this.lane.categories[category].sort().forEach(subCategory => {
        this.subCategoryKeys.push([category, subCategory]);
      });
    });

    if (this.lane.useNestedMenu) {
      Object.keys(this.lane.categories)
        .sort()
        .forEach(category => {
          this.nestedSubCategoryKeys.push([category, this.lane.categories[category].sort()]);
        });
    }

    this.isTypeModalMode =
      this.subCategoryKeys.length === 1 &&
      this.subCategoryKeys[0][0].toLowerCase() === "furniture" &&
      this.typeModalSubCategories.some(i => i === this.subCategoryKeys[0][1].toLowerCase());

    this.setItem();

    this._visionboardItemAddRemoveButtonService.removeItemClicked$
      .pipe(
        concatMap((input: VisionboardItemAddRemoveClickInput) => {
          return this.visionboardService.confirmItemDeleteOrReplace$(this.matDialog).pipe(
            tap(result => {
              if (result.result) {
                this.visionboardWorkspaceItemsStoreService.removeItem(input.item, true);
                this.updateItem(null, true);
              }
            }),
          );
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.hasOrderManagement = this._eventContextService.hasOrderManagementAccess();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  promptItemSelection(triggerOnboarding?: boolean): void {
    this.triggerOnboarding = triggerOnboarding;

    if (this.acceptsInput && !this.item) {
      if (this.isTypeModalMode) {
        this.showTableTypeModal();
      } else if (this.multiSubCatMode) {
        if (!this.lane.useNestedMenu) {
          this.multiSubCategoryMatMenuTrigger.openMenu();
        } else {
          this.nestedMultiSubCategoryMatMenuTrigger.openMenu();
        }
      } else {
        const subCategoryKey = this.subCategoryKeys[0];
        LoggingService.log(`VisionboardWorkspaceTemplateLaneItemComponent - onClick - subcat: ${subCategoryKey}`);

        this.showItemsDialog(subCategoryKey);
      }
    } else if (this.item) {
      this._visionboardItemDetailService.showItemDetail(
        {
          item: this.item,
        },
        this._vcr,
        this.matDialog,
        "VB Category Template Lane Item",
      );
    }
  }

  onSubCatClick(subCategoryKey: [string, string]): void {
    LoggingService.log(`VisionboardWorkspaceTemplateLaneItemComponent - onSubCatClick - subcat: ${subCategoryKey}`);
    this.showItemsDialog(subCategoryKey);
  }

  setItem(): void {
    this.updateItem(this.lane.items[this.index], false);
  }

  toggleControls(isHovered: boolean): void {
    this.isHovered = isHovered;
  }

  clearItem(): void {
    this.updateItem(null, true);
  }

  private showTableTypeModal(): void {
    const info = this.buildTableSelectionDialogInfo();
    const dialog = RadioButtonSelectionDialogComponent.show(info, this.matDialog);

    LoggingService.log("VisionBoardSelectTableTypeDialogComponent - Dialog opened");

    dialog
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        LoggingService.log(`VisionBoardSelectTableTypeDialogComponent - item dialog closed - type: ${result}`);

        if (result) {
          this.visionboardAttributesService.setTempAttributeOverrideByValue("Type", result);
          this.showItemsDialog(this.subCategoryKeys[0]);
        } else {
          this.triggerOnboardingInner();
        }
      });
  }

  private triggerOnboardingInner(): void {
    if (this.triggerOnboarding && !this.disableChecks) {
      this.onboardingService.trigger(OnboardingWorkflowStepTypes.VbItem);
      this.triggerOnboarding = false;
    }
  }

  private buildTableSelectionDialogInfo(): RadioButtonSelectionInfo {
    const headers = { Tables: "Table", Bars: "Bar" };
    const subCategory = this.subCategoryKeys[0][1];
    const header = headers[subCategory] || subCategory;

    return {
      title: `What type of ${header.toLowerCase()} are you looking for?`,
      buttonLabel: "Apply",
      options: [
        { title: `${header} with Linen​`, subtitle: `${subCategory} that you will apply a linen to​`, value: "Folding" },
        { title: `Specialty ${header}`, subtitle: `${subCategory} that do not require a linen​`, value: "Specialty" },
      ],
    };
  }

  private showCompatibleItemSelectionDialog(item: VisionboardItem): Observable<boolean> {
    const dialog = this.matDialog.open(VisionboardCompatibleItemSelectionDialogComponent, {
      data: { parentItem: item } as CompatibleItemSelectionInput,
      autoFocus: false,
    });

    LoggingService.log("VisionboardCompatibleItemSelectionDialogComponent - Dialog opened");

    return dialog.afterClosed().pipe(
      map(() => {
        const items = dialog.componentInstance.lane.items;
        this.visionboardWorkspaceTemplateService.addItemsToLane(items);
        return true;
      }),
    );
  }

  private showItemsDialog(subCategoryKey: [string, string]): void {
    // if this lane has filters, only apply those filters
    // else, apply dependency filters
    if (Object.keys(this.lane.filters).length > 0) {
      this.visionboardAttributesService.setTempAttributeOverridesByMap(this.lane.filters);
    } else {
      const parentFilters = this.visionboardItemCompatibilityService.getParentAttributeTypeValues(subCategoryKey[0], subCategoryKey[1]);

      this.visionboardAttributesService.setTempAttributeOverridesByValues("Type", parentFilters);
    }

    if (this.visionboardCategoriesService.setSelectedCategoryBySubCategory(subCategoryKey)) {
      const dialog = this.matDialog.open(VisionboardItemSelectionDialogComponent, {
        data: {
          forceVisionboardItemAddRemoveButton: ForceVisionboardItemAddRemoveButton.Add,
        },
        autoFocus: false,
        viewContainerRef: this._vcr,
      });

      LoggingService.log("VisionboardItemSelectionDialogComponent - Dialog opened");

      dialog
        .afterClosed()
        .pipe(take(1))
        .subscribe(result => {
          this.visionboardCategoriesService.setSelectedCategory(null);

          const item = result as VisionboardItem;

          if (item) {
            LoggingService.log(`VisionboardWorkspaceTemplateLaneItemComponent - item dialog closed - item selected: ${item.variantSkuId}`);

            item.templateOrder = this.index;

            if (this.item) {
              item.location = this.item.location;
              item.locationPct = this.item.locationPct;

              this.visionboardService
                .confirmItemDeleteOrReplace$(this.matDialog, true)
                .pipe(
                  tap(result => {
                    if (result.result) {
                      this.visionboardWorkspaceItemsStoreService.removeItem(this.item, true);
                      this.addItem(item);
                    }
                  }),
                )
                .subscribe();
            } else {
              this.addItem(item);
            }
          } else {
            LoggingService.log("VisionboardWorkspaceTemplateLaneItemComponent - item dialog closed - no item selected");
            this.triggerOnboardingInner();
          }
        });
    }
  }

  private _checkMaxItemsAdded(): Observable<boolean> {
    if (
      this.lane.items.filter(item => item !== null).length === this.lane.numItems &&
      this.onboardingService.stepComplete(OnboardingWorkflowStepTypes.VbFinal)
    ) {
      const dialog = this.matDialog.open(PromptConfirmDialogComponent, {
        restoreFocus: false,
        data: {
          title: "Max Items on Category VB",
          message: `You have reached the max number of ${
            this.lane.title ? this.lane.title.toLocaleLowerCase() : "items"
          }. You can add more in the Freeform view.`,
          yesText: "Go to Freeform",
          noText: "Close",
        } as PromptConfirmInput,
      });

      return dialog.afterClosed().pipe(
        map((result: PromptConfirmResult) => {
          if (result === PromptConfirmResult.Yes) {
            this.visionboardService.changeMode(VisionboardMode.Freeform);
            this.visionboardService.disableModeChange(false);
            return false;
          } else {
            return true;
          }
        }),
      );
    } else {
      return of(true);
    }
  }

  private addItem(item: VisionboardItem): void {
    this.visionboardWorkspaceItemsStoreService.addItem(item);
    this.updateItem(item, true);

    let obs: Observable<boolean>;

    if (this.disableChecks) {
      obs = of(false);
    } else if (this.visionboardItemCompatibilityService.requiresChildItems(item)) {
      obs = this.showCompatibleItemSelectionDialog(item);
    } else if (item.resizable) {
      obs = this.visionboardService.showResizablePrompt$(this.matDialog);
    } else {
      obs = of(true);
    }

    obs
      .pipe(
        concatMap(result => {
          return result ? this._checkMaxItemsAdded() : of(result);
        }),
      )
      .subscribe({
        next: result => {
          if (result) {
            this.triggerOnboardingInner();
          }
        },
      });
  }

  private updateItem(item: VisionboardItem, raiseLaneItemsUpdated: boolean): void {
    this.item = item;

    this.acceptsInput = this.subCategoryKeys.length > 0;

    if (item) {
      this.removable = !item.isVenueProvided;
      this.isVenueProvided = item.isVenueProvided;
      this.acceptsInput = this.removable && this.acceptsInput;
      this.tooltipText = null;
    } else {
      this.tooltipText = this.acceptsInput ? "Click to add" : "";
      this.removable = false;
      this.isVenueProvided = false;
    }

    this.multiSubCatMode = this.subCategoryKeys.length > 1;

    this.isOrderable = this.item?.isOrderable();

    this.itemChange.emit([this.index, this.item]);

    if (raiseLaneItemsUpdated) {
      this.visionboardWorkspaceTemplateService.raiseLaneItemsUpdated(this.lane);
    }
  }
}
